import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  useReconcileRuns,
  asReconcileScope,
  asReconcileStats,
  asReconcileLocationStats,
  phantomCandidateTotal,
  statNum,
  VARIANT_LINKS_ARCHIVE_THRESHOLD,
  type ReconcileRunStatus,
  type WireReconcileRun,
} from '../hooks/useReconcile';

/** Column count, so the expanded detail row spans the whole table. */
const HISTORY_COLUMN_COUNT = 8;

/** Status pill — semantic colour + glyph (colour-as-only-information is banned). */
function ReconcileStatusPill({ status }: { status: ReconcileRunStatus }) {
  const { t } = useTranslation('inventory');

  switch (status) {
    case 'queued':
    case 'running':
      return (
        <Badge className="gap-1 bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
          <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {status === 'queued'
            ? t('stockSync.reconcile.status.queued', 'Queued')
            : t('stockSync.reconcile.status.running', 'Running')}
        </Badge>
      );
    case 'completed':
      return (
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs">
          {t('stockSync.reconcile.status.completed', 'Completed')}
        </Badge>
      );
    case 'aborted_safeguard':
      return (
        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">
          {t('stockSync.reconcile.status.aborted', 'Paused for safety')}
        </Badge>
      );
    case 'failed':
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-xs">
          {t('stockSync.reconcile.status.failed', 'Failed')}
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge variant="outline" className="text-xs text-muted-foreground">
          {t('stockSync.reconcile.status.cancelled', 'Cancelled')}
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-xs">
          {status}
        </Badge>
      );
  }
}

export function ReconcileRunHistory() {
  const { t } = useTranslation('inventory');
  const { data, isLoading } = useReconcileRuns(20);
  const runs: WireReconcileRun[] = data ?? [];
  // Which runs have their per-location "would zero" breakdown open. In-memory
  // only (DESIGN.md 14.1) — a run's expansion is a reading aid, not a setting.
  const [expandedRunIds, setExpandedRunIds] = useState<ReadonlySet<string>>(new Set());
  const toggleExpanded = (runId: string) =>
    setExpandedRunIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(runId)) next.add(runId);
      return next;
    });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-10 rounded-lg" />
        ))}
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <p className="text-sm font-medium">
          {t('stockSync.reconcile.history.emptyTitle', 'No reconcile runs yet')}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            'stockSync.reconcile.history.emptyDesc',
            "Run a reconcile to correct Shopify stock that's drifted from PrintRun.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('stockSync.reconcile.history.colWhen', 'When')}</TableHead>
            <TableHead>{t('stockSync.reconcile.history.colScope', 'Scope')}</TableHead>
            <TableHead>{t('stockSync.reconcile.history.colStatus', 'Status')}</TableHead>
            <TableHead className="text-right">
              {t('stockSync.reconcile.history.colChecked', 'Checked')}
            </TableHead>
            <TableHead className="text-right">
              {t('stockSync.reconcile.history.colCorrected', 'Corrected')}
            </TableHead>
            {/* BLU-715b. The qualifier is in the header, not a tooltip: this
                number is the case FOR arming a location, and a number whose
                caveat is hover-only will be read as a forecast. */}
            <TableHead className="text-right">
              <span className="block">
                {t('stockSync.reconcile.history.colWouldZero', 'Would zero')}
              </span>
              <span className="block text-[10px] font-normal text-muted-foreground">
                {t('stockSync.reconcile.history.colWouldZeroHint', 'if armed, upper bound')}
              </span>
            </TableHead>
            <TableHead className="text-right">
              {t('stockSync.reconcile.history.colLinks', 'Links Δ')}
            </TableHead>
            <TableHead>{t('stockSync.reconcile.history.colBy', 'By')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map((run) => {
            const scope = asReconcileScope(run.scope);
            const stats = asReconcileStats(run.stats);
            const upserted = statNum(stats, 'linksUpserted');
            const archived = statNum(stats, 'linksArchived');
            const archivedLarge = archived >= VARIANT_LINKS_ARCHIVE_THRESHOLD;
            const checked = statNum(stats, 'variantsChecked');
            const corrected = statNum(stats, 'corrected');
            const whenStamp = run.created_at;
            // BLU-715b — what arming a location would have cost on this run.
            // `wouldZero` is the raw candidate class (upper bound, pre-veto);
            // `blockedByAuthority` is veto-applied and therefore exact, but only
            // counts the locations that were NOT armed. Normally the first is
            // the larger of the two; `Math.max` only guards the case where an
            // older run carries the scalar without the per-location array, so
            // the headline can never sit BELOW a count we know was held back.
            const wouldZero = phantomCandidateTotal(stats);
            const blockedByAuthority = statNum(stats, 'suppressedByNoAuthority');
            const wouldZeroHeadline = Math.max(wouldZero, blockedByAuthority);
            const wouldZeroByLocation = asReconcileLocationStats(stats).filter(
              (l) => l.phantomCandidates > 0,
            );
            const expanded = expandedRunIds.has(run.id);
            const detailId = `reconcile-would-zero-${run.id}`;

            return (
              <Fragment key={run.id}>
                <TableRow>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(whenStamp), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    {scope?.skuPrefix ? (
                      <Badge variant="outline" className="font-mono text-xs">
                        {scope.skuPrefix}
                      </Badge>
                    ) : scope?.skuIds ? (
                      <Badge variant="outline" className="text-xs">
                        {t('stockSync.reconcile.history.scopeTargeted', '{{count}} SKUs', {
                          count: scope.skuIds.length,
                        })}
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        {t('stockSync.reconcile.history.scopeFull', 'Full')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <ReconcileStatusPill status={run.status} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {checked > 0 ? checked.toLocaleString() : '-'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {corrected > 0 ? corrected.toLocaleString() : '-'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums whitespace-nowrap">
                    {wouldZeroHeadline === 0 ? (
                      <span className="text-muted-foreground">-</span>
                    ) : (
                      // inline-flex, not flex: a block-level flex container fills
                      // the cell and the cell's text-right stops working.
                      <button
                        type="button"
                        onClick={() => toggleExpanded(run.id)}
                        aria-expanded={expanded}
                        aria-controls={detailId}
                        className="inline-flex items-center gap-1 rounded-sm px-1 py-0.5 font-medium text-amber-700 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <ChevronDown
                          className={cn(
                            'h-3 w-3 transition-transform duration-200 motion-reduce:transition-none',
                            !expanded && '-rotate-90',
                          )}
                          aria-hidden="true"
                        />
                        {wouldZeroHeadline.toLocaleString()}
                        {/* No "show"/"hide" verb in the name — aria-expanded
                            already carries the state, and a hard-coded verb
                            goes stale the moment the row is open. */}
                        <span className="sr-only">
                          {' '}
                          {t(
                            'stockSync.reconcile.history.wouldZeroToggle',
                            'Shopify levels that would be zeroed if these locations were armed. Breakdown by location.',
                          )}
                        </span>
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums whitespace-nowrap">
                    {upserted === 0 && archived === 0 ? (
                      '-'
                    ) : (
                      <span>
                        <span className="text-emerald-600">+{upserted.toLocaleString()}</span>
                        <span className="text-muted-foreground"> / </span>
                        <span className={cn(archivedLarge ? 'text-amber-700' : 'text-muted-foreground')}>
                          −{archived.toLocaleString()}
                        </span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {run.triggered_by === 'cron' ? (
                      <Badge variant="outline" className="text-xs text-muted-foreground">
                        {t('stockSync.reconcile.history.byScheduled', 'Scheduled')}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        {t('stockSync.reconcile.history.byManual', 'Manual')}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>

                {/* Per-location breakdown. Kept in a disclosure rather than a
                    tooltip so touch and screen-reader users get the same
                    numbers, and per LOCATION because authority is armed per
                    location: an org-wide total cannot answer "which floor?". */}
                {expanded && wouldZeroHeadline > 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={HISTORY_COLUMN_COUNT} className="bg-amber-50/50 p-0">
                      <div
                        id={detailId}
                        className="space-y-2 px-3 py-2.5 motion-safe:animate-fade-in"
                      >
                        <p className="text-xs font-medium text-amber-900">
                          {t(
                            'stockSync.reconcile.history.wouldZeroHeading',
                            'Shopify levels that would be set to 0 if these locations were armed',
                          )}
                        </p>
                        {wouldZeroByLocation.length > 0 && (
                          <ul className="space-y-1">
                            {wouldZeroByLocation.map((loc) => (
                              <li
                                key={loc.locationId}
                                className="flex items-baseline justify-between gap-4 text-xs"
                              >
                                <span className="min-w-0 truncate text-foreground">
                                  {loc.location}
                                </span>
                                <span className="shrink-0 tabular-nums font-medium text-amber-800">
                                  {t(
                                    'stockSync.reconcile.history.wouldZeroLevels',
                                    '{{count}} level',
                                    { count: loc.phantomCandidates },
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {blockedByAuthority > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {t(
                              'stockSync.reconcile.history.wouldZeroBlocked',
                              '{{count}} of these passed every other check on this run and was held back only because its location is not armed.',
                              { count: blockedByAuthority },
                            )}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {t(
                            'stockSync.reconcile.history.wouldZeroCaveat',
                            'Upper bound. These are levels holding Shopify stock PrintRun has no record of, counted before the proven-writer and hybrid checks a live run also applies, so fewer levels may actually be zeroed.',
                          )}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
